import React, { useEffect, useState } from 'react';
import { Pause, Play, X, Clock } from 'lucide-react';
import type { Timer } from '@/types/clock';

interface TimerDisplayProps {
  timer: Timer;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
  accentColor: string;
  textColor: string;
}

const formatTimeRemaining = (ms: number): string => {
  const totalSeconds = Math.ceil(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const formatDuration = (ms: number): string => {
  const totalMinutes = Math.ceil(ms / 60000);
  if (totalMinutes >= 60) {
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${totalMinutes}m`;
};

export const TimerDisplay: React.FC<TimerDisplayProps> = ({
  timer,
  onPause,
  onResume,
  onCancel,
  accentColor,
  textColor
}) => {
  const [remaining, setRemaining] = useState(0);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (!timer.running) {
      setRemaining(timer.pausedAt || 0);
      setProgress((timer.pausedAt || 0) / timer.durationMs * 100);
      return;
    }

    const updateRemaining = () => {
      const now = Date.now();
      const rem = Math.max(0, timer.targetTs - now);
      setRemaining(rem);
      setProgress((rem / timer.durationMs) * 100);
    };

    updateRemaining();
    const interval = setInterval(updateRemaining, 100); // Update 10 times per second for smooth animation

    return () => clearInterval(interval);
  }, [timer, timer.running, timer.targetTs, timer.durationMs]);

  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  const isLowTime = remaining < 10000;
  const progressPercentage = Math.round(progress);

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center rounded-3xl overflow-hidden">
      {/* Animated gradient background */}
      <div 
        className="absolute inset-0 opacity-95"
        style={{
          background: `radial-gradient(circle at center, ${accentColor}08 0%, transparent 60%)`
        }}
      />
      
      {/* Glassmorphism overlay */}
      <div className="absolute inset-0 backdrop-blur-xl bg-gradient-to-br from-black/60 via-black/50 to-black/60" />
      
      {/* Animated rings background */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="absolute rounded-full border opacity-5"
            style={{
              borderColor: accentColor,
              width: `${300 + i * 100}px`,
              height: `${300 + i * 100}px`,
              animation: `pulse ${3 + i}s ease-in-out infinite`,
              animationDelay: `${i * 0.5}s`
            }}
          />
        ))}
      </div>

      {/* Content container */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Timer label with icon */}
        {timer.label && (
          <div className="flex items-center gap-2 mb-4 px-6 py-2 rounded-full bg-white/5 backdrop-blur-sm border border-white/10">
            <Clock className="w-4 h-4 text-white/60" />
            <span className="text-sm font-medium text-white/80 tracking-wide uppercase">
              {timer.label}
            </span>
          </div>
        )}

        {/* Large circular progress indicator */}
        <div className="relative mb-8">
          <svg width="280" height="280" className="transform -rotate-90">
            {/* Outer glow ring */}
            <circle
              cx="140"
              cy="140"
              r="130"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              className="opacity-10"
              style={{ color: accentColor }}
            />
            
            {/* Background circle */}
            <circle
              cx="140"
              cy="140"
              r={radius + 45}
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              className="opacity-10"
              style={{ color: accentColor }}
            />
            
            {/* Progress circle with glow */}
            <circle
              cx="140"
              cy="140"
              r={radius + 45}
              fill="none"
              stroke="url(#progressGradient)"
              strokeWidth="6"
              strokeLinecap="round"
              style={{
                strokeDasharray: circumference * 1.64,
                strokeDashoffset: (circumference * 1.64) - ((progress / 100) * (circumference * 1.64)),
                transition: timer.running ? 'stroke-dashoffset 0.1s linear' : 'stroke-dashoffset 0.3s ease-out',
                filter: `drop-shadow(0 0 12px ${accentColor}) drop-shadow(0 0 24px ${accentColor}60)`
              }}
            />
            
            {/* Define gradient */}
            <defs>
              <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={accentColor} stopOpacity="1" />
                <stop offset="100%" stopColor={accentColor} stopOpacity="0.6" />
              </linearGradient>
            </defs>
          </svg>

          {/* Time remaining - centered */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div 
              className="text-7xl font-bold tabular-nums tracking-tight"
              style={{ 
                color: accentColor,
                textShadow: `0 0 40px ${accentColor}40, 0 4px 20px rgba(0,0,0,0.5)`,
                fontFamily: 'var(--font-satoshi), system-ui, sans-serif',
                transition: 'all 0.3s ease'
              }}
            >
              {formatTimeRemaining(remaining)}
            </div>
            
            {/* Progress percentage */}
            <div className="mt-3 text-lg font-medium text-white/40 tracking-wider">
              {progressPercentage}%
            </div>
            
            {/* Duration info */}
            <div className="mt-2 px-4 py-1 rounded-full bg-white/5 backdrop-blur-sm">
              <span className="text-xs text-white/50 font-medium">
                {formatDuration(timer.durationMs)} timer
              </span>
            </div>
          </div>
        </div>

        {/* Status indicator */}
        <div className="mb-6 flex items-center gap-2 text-white/60 text-sm font-medium">
          <div 
            className={`w-2 h-2 rounded-full ${
              timer.running ? 'bg-green-400 animate-pulse' : 'bg-yellow-400'
            }`}
            style={{
              boxShadow: timer.running ? `0 0 12px ${accentColor}` : '0 0 12px #fbbf24'
            }}
          />
          <span>{timer.running ? 'Running' : 'Paused'}</span>
        </div>

        {/* Elegant control buttons */}
        <div className="flex items-center gap-4">
          {timer.running ? (
            <button
              onClick={onPause}
              className="group relative px-8 py-4 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 hover:from-white/20 hover:to-white/10 backdrop-blur-lg border border-white/20 transition-all duration-300 hover:scale-105 hover:shadow-2xl overflow-hidden"
              title="Pause Timer"
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              
              <div className="relative flex items-center gap-3">
                <Pause className="w-5 h-5 text-white" />
                <span className="text-white font-semibold tracking-wide">Pause</span>
              </div>
            </button>
          ) : (
            <button
              onClick={onResume}
              className="group relative px-8 py-4 rounded-2xl bg-gradient-to-br from-white/10 to-white/5 hover:from-white/20 hover:to-white/10 backdrop-blur-lg border border-white/20 transition-all duration-300 hover:scale-105 hover:shadow-2xl overflow-hidden"
              title="Resume Timer"
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              
              <div className="relative flex items-center gap-3">
                <Play className="w-5 h-5 text-white" />
                <span className="text-white font-semibold tracking-wide">Resume</span>
              </div>
            </button>
          )}
          
          <button
            onClick={onCancel}
            className="group relative px-8 py-4 rounded-2xl bg-gradient-to-br from-red-500/20 to-red-600/10 hover:from-red-500/30 hover:to-red-600/20 backdrop-blur-lg border border-red-500/30 hover:border-red-500/50 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-red-500/20 overflow-hidden"
            title="Cancel Timer"
          >
            {/* Shimmer effect */}
            <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-red-400/10 to-transparent" />
            
            <div className="relative flex items-center gap-3">
              <X className="w-5 h-5 text-red-400" />
              <span className="text-red-300 font-semibold tracking-wide">Cancel</span>
            </div>
          </button>
        </div>
      </div>

      {/* Urgent pulsing effect when time is low */}
      {timer.running && isLowTime && (
        <>
          <div 
            className="absolute inset-0 rounded-3xl pointer-events-none animate-pulse"
            style={{
              background: `radial-gradient(circle at center, ${accentColor}15 0%, transparent 60%)`,
            }}
          />
          <div 
            className="absolute inset-0 rounded-3xl pointer-events-none"
            style={{
              boxShadow: `inset 0 0 80px ${accentColor}20`,
              animation: 'pulse 0.5s ease-in-out infinite'
            }}
          />
        </>
      )}
    </div>
  );
};
